import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../services/authService';

// Şifre kuralları
const PASSWORD_RULES = [
  { key: 'length',    label: 'En az 8 karakter',          test: (p) => p.length >= 8 },
  { key: 'uppercase', label: 'En az 1 büyük harf (A-Z)',   test: (p) => /[A-Z]/.test(p) },
  { key: 'lowercase', label: 'En az 1 küçük harf (a-z)',   test: (p) => /[a-z]/.test(p) },
  { key: 'number',    label: 'En az 1 rakam (0-9)',         test: (p) => /[0-9]/.test(p) },
  { key: 'special',   label: 'En az 1 özel karakter (!@#$%^&* vb.)', test: (p) => /[^A-Za-z0-9]/.test(p) },
];

const validatePassword = (password) => PASSWORD_RULES.map(r => ({ ...r, passed: r.test(password) }));
const isPasswordValid  = (password) => PASSWORD_RULES.every(r => r.test(password));

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword]       = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword]     = useState(false);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState('');
  const [success, setSuccess]               = useState(false);
  const [touched, setTouched]               = useState(false); // şifre alanına dokunuldu mu?

  const token        = searchParams.get('token');
  const email        = searchParams.get('email');
  const isActivation = searchParams.get('isActivation') === 'true';

  const rules         = validatePassword(newPassword);
  const passwordValid = isPasswordValid(newPassword);
  const passedCount   = rules.filter(r => r.passed).length;

  useEffect(() => {
    if (!token || !email) setError('Geçersiz veya eksik link.');
  }, [token, email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!passwordValid) {
      setError('Şifreniz tüm güvenlik kurallarını karşılamalıdır.');
      setTouched(true);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Şifreler eşleşmiyor.');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, newPassword);
      setSuccess(true);
    } catch {
      setError(
        isActivation
          ? 'Geçersiz veya süresi dolmuş link. Lütfen portal giriş sayfasındaki "İlk kez giriş yapıyorum" seçeneğini kullanın.'
          : 'Geçersiz veya süresi dolmuş link. Lütfen tekrar şifre sıfırlama talebinde bulunun.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Güç göstergesi rengi
  const strengthColor = passedCount <= 2 ? '#e53e3e' : passedCount <= 3 ? '#f6ad55' : passedCount === 4 ? '#68d391' : '#38a169';
  const strengthLabel = passedCount <= 2 ? 'Zayıf' : passedCount <= 3 ? 'Orta' : passedCount === 4 ? 'İyi' : 'Güçlü';

  // ─── Başarı ekranı ────────────────────────────────────────────────
  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✓</div>
          <h2 style={styles.title}>
            {isActivation ? 'Şifreniz Belirlendi' : 'Şifre Güncellendi'}
          </h2>
          <p style={styles.successMessage}>
            {isActivation
              ? 'Şifreniz başarıyla belirlendi. Artık sisteme giriş yapabilirsiniz.'
              : 'Şifreniz başarıyla güncellendi.'}
          </p>
          <button style={styles.button} onClick={() => navigate('/login')}>
            Giriş Yap
          </button>
        </div>
      </div>
    );
  }

  // ─── Şifre belirleme formu ────────────────────────────────────────
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>
          {isActivation ? 'Şifrenizi Belirleyin' : 'Yeni Şifre Belirle'}
        </h2>
        {isActivation && (
          <p style={styles.description}>
            Hoş geldiniz! Hesabınıza erişmek için lütfen bir şifre belirleyin.
          </p>
        )}

        <form onSubmit={handleSubmit}>
          {/* Şifre alanı */}
          <div style={styles.field}>
            <label style={styles.label}>
              {isActivation ? 'Şifre' : 'Yeni Şifre'}
            </label>
            <div style={styles.inputWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={e => { setNewPassword(e.target.value); setTouched(true); }}
                style={styles.input}
                required
                autoFocus
              />
              <button
                type="button"
                style={styles.eyeButton}
                onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {/* Güç göstergesi — şifreye dokunulunca görünür */}
          {touched && newPassword.length > 0 && (
            <div style={{ marginBottom: '14px' }}>
              {/* Progress bar */}
              <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} style={{
                    flex: 1, height: '4px', borderRadius: '2px',
                    backgroundColor: i <= passedCount ? strengthColor : '#e2e8f0',
                    transition: 'background-color 0.2s'
                  }} />
                ))}
              </div>
              <div style={{ fontSize: '11px', color: strengthColor, fontWeight: '600', marginBottom: '8px' }}>
                {strengthLabel}
              </div>
              {/* Kural listesi */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {rules.map(r => (
                  <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: r.passed ? '#38a169' : '#e53e3e' }}>
                    <span style={{ fontSize: '10px' }}>{r.passed ? '✓' : '✗'}</span>
                    {r.label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Şifre tekrar */}
          <div style={styles.field}>
            <label style={styles.label}>Şifre Tekrar</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              style={{
                ...styles.input,
                borderColor: confirmPassword && newPassword !== confirmPassword ? '#e53e3e' : '#d9d9d9'
              }}
              required
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <span style={{ fontSize: '11px', color: '#e53e3e', marginTop: '2px' }}>Şifreler eşleşmiyor</span>
            )}
          </div>

          {error && <p style={styles.error}>{error}</p>}

          <button
            type="submit"
            style={{
              ...styles.button,
              opacity: loading || !token || !passwordValid || newPassword !== confirmPassword ? 0.6 : 1,
              cursor: loading || !token ? 'not-allowed' : 'pointer'
            }}
            disabled={loading || !token}
          >
            {loading
              ? 'Kaydediliyor...'
              : isActivation
              ? 'Şifremi Belirle'
              : 'Şifremi Güncelle'}
          </button>
        </form>

        <p style={styles.backLink}>
          <span style={styles.link} onClick={() => navigate('/login')}>
            ← Giriş sayfasına dön
          </span>
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f0f2f5',
  },
  card: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    width: '380px',
  },
  successIcon: {
    textAlign: 'center',
    fontSize: '48px',
    color: '#38a169',
    marginBottom: '12px',
  },
  title: {
    textAlign: 'center',
    marginBottom: '8px',
    color: '#1a1a2e',
    fontSize: '20px',
  },
  description: {
    textAlign: 'center',
    fontSize: '13px',
    color: '#555',
    marginBottom: '20px',
    lineHeight: '1.6',
  },
  successMessage: {
    textAlign: 'center',
    color: '#555',
    fontSize: '14px',
    marginBottom: '24px',
    lineHeight: '1.6',
  },
  field: {
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '13px',
    color: '#333',
    fontWeight: '500',
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    width: '100%',
    padding: '10px',
    paddingRight: '36px',
    borderRadius: '4px',
    border: '1px solid #d9d9d9',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  eyeButton: {
    position: 'absolute',
    right: '8px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '0',
    lineHeight: '1',
  },
  error: {
    color: '#e53e3e',
    fontSize: '13px',
    marginBottom: '8px',
    lineHeight: '1.5',
  },
  button: {
    width: '100%',
    padding: '11px',
    backgroundColor: '#1a1a2e',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '15px',
    cursor: 'pointer',
    marginTop: '4px',
    transition: 'opacity 0.2s',
  },
  backLink: {
    textAlign: 'center',
    marginTop: '16px',
    fontSize: '13px',
  },
  link: {
    color: '#1a1a2e',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
};

export default ResetPasswordPage;